'use client';

import classNames from 'classnames';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { usePlausible } from 'next-plausible';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { FaqEvents } from '@/lib/tracking';
import { segment } from '@/lib/tracking/segmentAnalytics';
import type { DataAttributes, Faq as FaqType } from '@/types';

import styles from './Faq.module.scss';

interface Props extends DataAttributes {
    className?: string;
    children: ReactNode;
    id: FaqType['id'];
    question: FaqType['question'];
}

export function Faq({ children, className, id, question, ...attributes }: Props) {
    const [isOpen, setIsOpen] = useState(false);

    const plausible = usePlausible();

    function toggleIsOpen() {
        const propertiesToTrack = {
            path: window.location.pathname,
            href: window.location.href,
            question,
            question_id: id,
        };
        const nextIsOpen = !isOpen;
        const trackingEvent = nextIsOpen ? FaqEvents.OPENED : FaqEvents.CLOSED;

        setIsOpen(nextIsOpen);

        segment.track(trackingEvent, propertiesToTrack);
        plausible(trackingEvent, { props: propertiesToTrack });
    }

    return (
        <div data-faq={id} {...attributes} className={classNames(styles.wrapper, className)}>
            <button className={styles.titleWrapper} onClick={toggleIsOpen}>
                <div className={styles.question} data-faq-question>
                    {question}
                </div>
                {isOpen ? (
                    <MinusIcon className={styles.icon} />
                ) : (
                    <PlusIcon className={styles.icon} />
                )}
            </button>
            {isOpen && (
                <div className={styles.answer} data-faq-answer>
                    {children}
                </div>
            )}
        </div>
    );
}
